import type { ComponentProps } from 'react';
import { useEffect, useMemo, useState } from 'react';
import {
  PROJECT_STATUSES,
  type Client,
  type Metafield,
  type Project,
  type ProjectViewCounts,
} from '@forecastify/shared';
import { DataList, type DataListColumn, type DataListRow } from '@forecastify/ui';
import { useDuration } from '../../duration';
import { useT } from '../../i18n';
import type { ListQuery } from '../use-list-query';
import { metafieldCells, metafieldColumns, metafieldFilters } from '../metafield-columns';
import { useAvatars } from '../use-avatar';
import { ListHeader, usePageHeader } from '../page-header';
import { useSidebarContext } from '../sidebar/use-sidebar';
import { RISK_FILTER_VALUES, riskDot, riskTitle } from '../alerts/risk-pill';
import { listLabels } from './clients-screen';
import { ProjectDialog, statusKey, type PickableMember, type ProjectDraft } from './project-dialog';
import type { ClientDraft } from './client-dialog';
import { StatusReasonDialog } from './status-reason-dialog';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Projektek — `markup.html` 460–470. The list view.                          │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **The mockup draws project CARDS as well as a list**, and this is the list. Its
 * `projectCards` block (main.jsx 2288–2306) computes a task count, a done percentage, late tasks
 * and an hours ratio for each card — **every one of which counts tasks (C8) or worklogs (C11)**.
 * A card grid rendering `0 / 0` and a flat progress bar for every project would look finished and
 * say nothing, which is the failure `contract.ts` refuses for the dashboard tab too.
 *
 * So C7 ships the columns it can fill honestly, and the cards arrive with their numbers. Recorded
 * here rather than left as a difference somebody spots later and assumes was an oversight.
 *
 * ⚠️ **Same in-memory search and filter as the client list, for the same bounded reason** — see
 * `clients-screen.tsx`. Both change together when either list paginates.
 */
/**
 * ⚠️ **`count` is OMITTED rather than sent as `0` when the server has not answered yet.** A tab
 * reading *Aktív 0* during the first load says something false; a tab reading *Aktív* says nothing,
 * which is the truth at that moment. `SavedView.count` is optional for exactly this.
 */
function countOf(
  counts: ProjectViewCounts | undefined,
  key: 'all' | 'active' | 'mine' | 'late' | 'data' | 'archived',
): { count?: number } {
  return counts === undefined ? {} : { count: counts[key] };
}

export function ProjectsScreen({
  mine = false,
  page,
  query,
  viewCounts,
  projects,
  clients,
  members,
  pmCandidates,
  pmFilterMembers,
  currentMemberId,
  canCreate,
  canManage,
  createRequested,
  onCreateConsumed,
  onCreate,
  onCreateClient,
  clientMetafields,
  onUpdate,
  onOpen,
  onFilterTasks,
  onTimeline,
  onReport,
  onArchive,
  onUnarchive,
  onExport,
  metafields,
  serverError,
  loading,
}: {
  /** Until the first answer: skeleton rows and no figures. */
  readonly loading?: boolean;
  /**
   * ⚠️ The page controls. Absent means the list is not paged — which is how every embedded copy of
   * this table stays unchanged (`state-rules.md` §6: consistent WITHIN a screen).
   */
  readonly page?: ComponentProps<typeof DataList>['page'];
  /**
   * ⚠️⚠️ **SEARCH, FILTERS, THE SAVED VIEW AND THE SORT ARE THE ROUTE'S NOW.** This screen held all
   * four in its own `useState` and filtered the array it was handed — CLAUDE.md §3's *"never
   * fetch-then-filter-in-memory"*, and visibly wrong once item 20 gave the list a real pager. Item
   * 35 moved them to the server; `use-list-query.ts` holds them.
   *
   * ⚠️ **The saved view is a FILTER now, not a fourth axis of its own.** *Aktív* sets the status,
   * *Az enyémek* sets the PM, *Csúszó feladattal* sets `late` — each of which the server already
   * understands. What the mockup called a separate axis was three filters wearing one control.
   */
  readonly query: ListQuery;
  /**
   * The four figures over the view tabs.
   *
   * ⚠️ **From the server, not from `projects`.** Counted here they would be *"how many on this
   * page"* wearing the label *"how many there are"* — `state-rules.md` §5.
   */
  readonly viewCounts?: ProjectViewCounts;
  /**
   * *Projektjeim* — the same list, opened from the sidebar's Projektek heading rather than from the
   * Workspace group. Changes the marker and the title; `routes.tsx` argues the route.
   */
  readonly mine?: boolean;
  readonly projects: readonly Project[];
  readonly clients: readonly Client[];
  readonly members: readonly PickableMember[];
  /**
   * 3 Nov 2026 · who may be named PM (`GET /members?can=project.manage`) — the dialog's PM field.
   * Absent, `members`.
   */
  readonly pmCandidates?: readonly PickableMember[];
  /**
   * The *Projektvezető* filter's options: the eligible people PLUS anybody who runs a project now,
   * so a project whose PM lost the right stays filterable. Absent, `members`.
   */
  readonly pmFilterMembers?: readonly PickableMember[];
  /**
   * Whose *Én vagyok a PM* view this is. `null` outside the chrome — the view is then absent
   * rather than empty, because a saved view that can never match anything is a dead control.
   */
  readonly currentMemberId?: string | null;
  /**
   * The sidebar's `+` asking for the create dialog. See the effect below.
   *
   * ⚠️ Optional, so every existing caller and every test keeps working without it.
   */
  readonly createRequested?: boolean;
  /** Called once the dialog has opened, so the route can clear the address. */
  readonly onCreateConsumed?: () => void;
  /** `project.create` — its own action, `decisions.md` R-12. */
  readonly canCreate: boolean;
  /** `project.manage`. */
  readonly canManage: boolean;
  /** Resolves to whether it landed; the dialog closes on `true` and keeps the form on `false`. */
  readonly onCreate: (draft: ProjectDraft) => Promise<boolean>;
  /** ⚠️ Item 21 · resolves to the new client's id, or `null` when it was refused. */
  readonly onCreateClient?: (draft: ClientDraft) => Promise<string | null>;
  /** The workspace's `client` metafields, for the client form the project dialog opens. */
  readonly clientMetafields?: readonly Metafield[];
  readonly onUpdate: (project: Project, draft: ProjectDraft) => Promise<boolean>;
  /** Open the detail page. The screen does not know about routing. */
  readonly onOpen: (project: Project) => void;
  /**
   * The four navigating items from the mockup's `projRowMenu`.
   *
   * ⚠️ **Each is a CALLBACK, not a path.** Every screen in this app is presentational and its
   * tests render it bare; a `navigate()` in here would need router context and break three of
   * them the way `useSearchParams` once did. The route owns addresses.
   */
  readonly onFilterTasks: (project: Project) => void;
  readonly onTimeline: (project: Project) => void;
  readonly onReport: (project: Project) => void;
  /**
   * ⚠️ **Archiving is a STATUS, not a delete.** `PROJECT_STATUSES` already has `archived`, so this
   * is the ordinary update path — the row's badge changes to *Archivált* and the list reloads,
   * which is why there is no confirmation step and nothing to undo but the same control.
   */
  /**
   * 3 Nov 2026 · **with the reason the person typed** — one for the whole selection. Resolves to
   * whether it landed (`false` keeps the reason dialog open); anything else closes it.
   */
  readonly onArchive: (projects: readonly Project[], reason: string) => unknown;
  /** 30 Oct 2026 · the way back, from the row — it was only on the project's own page. */
  readonly onUnarchive: (projects: readonly Project[], reason: string) => unknown;
  /** T-10's shared export path, given the selection. */
  readonly onExport: (projects: readonly Project[]) => void;
  /** The workspace's metafield definitions for this entity — passed straight to the dialog. */
  readonly metafields?: readonly Metafield[];
  readonly serverError: string | null;
}) {
  const t = useT();
  const { fmt } = useDuration();
  /**
   * ⚠️ **The primary action lives in the HEADER**, which is where the mockup puts it — see
   * `ListHeader`. It used to sit in the list toolbar, a row lower and mixed in with the filters.
   */
  /**
   * ┌───────────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ THE CREATE ACTION IS IN THE TOOLBAR, NOT THE HEADER — item 25, 15 Sep.│
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * *"They all should look like contracts, one line with the search, not purple."* Contracts have
   * passed their create through `DataList`'s `actions` since C30 and every other list grew a purple
   * header CTA instead, so the same act looked like two different things depending on which list
   * you were standing in.
   *
   * ⚠️ **The header keeps the TITLE and nothing else.** A header action is right for a screen whose
   * primary act is not about a list — a task detail, a project — and wrong here, where the toolbar
   * already holds the search, the filters and the columns that the create belongs beside.
   */
  usePageHeader(
    () => <ListHeader title={t(mine ? 'title.myProjects' : 'title.projects')} />,
    [mine],
  );

  /* The shared sidebar state — null on any screen rendered outside the chrome. */
  const sidebar = useSidebarContext();
  const [editing, setEditing] = useState<Project | null>(null);
  /**
   * 3 Nov 2026 · an archive or un-archive waiting for its reason — one reason for the whole
   * selection. Every archive is a status change, and every status change says why.
   */
  const [pendingArchive, setPendingArchive] = useState<{
    readonly projects: readonly Project[];
    readonly archived: boolean;
  } | null>(null);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const askArchive = (selection: readonly Project[], archived: boolean): void => {
    if (selection.length > 0) setPendingArchive({ projects: selection, archived });
  };
  /* ⚠️ The saved view rides in the filters, so *"which view is selected"* has one home. */
  const view = query.filters.view ?? 'all';
  const [dialogOpen, setDialogOpen] = useState(false);

  /**
   * ⚠⚠️ **THE SIDEBAR'S `+` ARRIVES AS A PROP, NOT AS A QUERY PARAMETER READ HERE.** Creating a
   * project is a dialog on this screen rather than an address, so the shell has no route to send
   * anybody to — it navigates to `/projects?new=1` and the ROUTE turns that into this flag.
   *
   * ⚠️ **The first version read the parameter here with `useSearchParams`, and that was wrong for a
   * reason the tests said out loud**: every screen in this app is presentational and its tests render
   * it bare, so reaching for router context made three of them fail with *"useLocation() may be used
   * only in the context of a <Router>"*. The route fetches and knows about addresses; the screen
   * renders props. Putting one `useSearchParams` in a screen quietly moved that line.
   */
  useEffect(() => {
    if (!createRequested || !canCreate) return;
    setEditing(null);
    setDialogOpen(true);
    onCreateConsumed?.();
  }, [createRequested, canCreate, onCreateConsumed]);

  /**
   * ⚠️ **`Készültség` and `Csúszó feladattal` need TASK counts, and the list holds none.** They
   * arrive on `projectSchema` as three `count(*)` subqueries — see the note there. Computing them
   * here would mean fetching every task of every project to draw one bar per row.
   */
  /* ⚠️ **Every row the server sent is a row that matched.** Re-filtering here would be the second,
     quieter half of the bug item 35 removes. */
  const matching = projects;

  /**
   * ⚠️⚠️ **SORTING IS THE CALLER'S, AND THIS LIST NEVER SUPPLIED IT.** `DataList` takes `sort` /
   * `onSortChange` as controlled props and does not reorder rows itself — so the `sortKey` on the
   * name column rendered a header button that toggled an arrow and moved nothing. Every sortable
   * column now has a comparator here.
   *
   * ⚠️ **Nulls sort LAST in both directions**, because a project with no end date is not "earlier
   * than everything"; it is unscheduled, and burying it under real dates in one direction and
   * floating it above them in the other reads as two different bugs.
   */
  /* ⚠️ **The SERVER sorts** — `PROJECT_SORT` is its catalogue, and `DataList` never reorders the
     rows it is handed (CLAUDE.md §3). Item 45. */
  const sorted = matching;

  /* ⚠️ One request per distinct picture for the whole list — see `use-avatar.ts`. */
  const avatars = useAvatars(projects.map((project) => project.pmAvatarUrl));

  const rows: DataListRow[] = useMemo(
    () =>
      sorted.map((project) => ({
        id: project.id,
        cells: {
          identifier: { text: project.identifier, mono: true, muted: true },
          name: {
            dot: project.color,
            text: project.name,
            bold: true,
            /* The mockup searches on the code and shows it on the card; the list keeps it beside
             * the name rather than spending a column on it. */
            sub: project.keyPrefix,
          },
          client: {
            /* ⚠️ *Belső projekt* — a name, not an em-dash. A project with no client is not a
             * project whose client is unknown, and the two must not read the same. */
            text: project.clientName ?? t('projects.internal'),
            muted: project.clientName === null,
          },
          status: {
            badge: {
              label: t(statusKey(project.status)),
              variant: statusBadge(project.status),
            },
          },
          pm: {
            text: project.pmName ?? '–',
            muted: project.pmName === null,
            ...(project.pmName === null
              ? {}
              : {
                  avatar: {
                    initials: initialsOf(project.pmName),
                    ...(project.pmAvatarSeed === null ? {} : { seed: project.pmAvatarSeed }),
                    title: project.pmName,
                    src: avatars.get(project.pmAvatarUrl ?? ''),
                  },
                }),
          },
          done: {
            progress: {
              value:
                project.taskCount === 0 ? 0 : (project.doneTaskCount / project.taskCount) * 100,
              color: project.color,
              label: t('projects.column.done'),
            },
            /* ⚠️ An em-dash when there are NO tasks: `0/0` is a ratio of nothing, not zero percent. */
            text:
              project.taskCount === 0
                ? '–'
                : t('projects.done.of', {
                    done: project.doneTaskCount,
                    total: project.taskCount,
                  }),
            mono: true,
            muted: true,
          },
          /**
           * C60 · ⚠⚠ **AN EM-DASH WHERE THE PROJECT HAS NEVER BEEN SCANNED, NOT A 100.** A
           * project with no findings scores full marks whether its data is clean or whether nobody
           * has looked, and a credibility column that reports the second as the first is worse
           * than no column. The scan chore fills these in; a project created between two of its
           * ticks legitimately has none yet.
           */
          risk:
            project.riskScore === null
              ? { text: t('risk.none'), mono: true, muted: true, title: riskTitle(t, null) }
              : {
                  dot: riskDot(project.riskScore),
                  text: String(project.riskScore),
                  mono: true,
                  /* ⚠️ Muted above 85 — a healthy score is a reassurance, not a finding, and a
                     column of confident green competes with the two rows that need attention. */
                  muted: project.riskScore >= 85,
                  title: riskTitle(t, project.riskScore),
                },
          hours: {
            text: `${fmt(project.trackedHours)} / ${fmt(project.estimateHours)}`,
            mono: true,
          },
          start: { text: project.startsOn ?? '–', mono: true, muted: true },
          end: { text: project.endsOn ?? '–', mono: true, muted: true },
          /* ⚠️ **Item 27 · the workspace's own fields**, for every definition whose `inList` is on. */
          ...metafieldCells(t, metafields ?? [], project.metafields),
        },
      })),
    [sorted, t, avatars, metafields, fmt],
  );

  /** `main.jsx` `projListColumns` — same eight, same widths, same right-alignment. */
  const columns: DataListColumn[] = [
    /* ⚠️ **Before the name, which is where the feedback asked for it** — *"ami a projekt listában
       is megjelenik a név előtt"*. Its own column rather than a second `sub` under the name: the
       key already lives there, and two machine strings stacked under a title is a cell nobody
       reads. No `sortKey` — it is random, so sorting by it sorts by nothing. */
    { key: 'identifier', label: t('list.identifier'), width: 120 },
    { key: 'name', label: t('projects.column.name'), sortKey: 'name' },
    { key: 'client', label: t('projects.column.client'), width: 180, sortKey: 'client' },
    { key: 'status', label: t('projects.column.status'), width: 140 },
    { key: 'pm', label: t('projects.column.pm'), width: 130, sortKey: 'pm' },
    { key: 'done', label: t('projects.column.done'), width: 190, sortKey: 'done' },
    /* C60 · the mockup's own place and width — between Készültség and Óra / becslés, 96px, right. */
    {
      key: 'risk',
      label: t('projects.column.risk'),
      width: 96,
      align: 'right',
      sortKey: 'risk',
    },
    {
      key: 'hours',
      label: t('projects.column.hours'),
      width: 140,
      align: 'right',
      sortKey: 'tracked_hours',
    },
    {
      key: 'start',
      label: t('projects.column.start'),
      width: 110,
      align: 'right',
      sortKey: 'starts_on',
    },
    { key: 'end', label: t('projects.column.end'), width: 110, align: 'right', sortKey: 'ends_on' },
    /* ⚠️ No `sortKey` — `metafield-columns.ts` argues it. */
    ...metafieldColumns(metafields ?? []),
  ];

  return (
    <div data-screen={mine ? 'my-projects' : 'projects'} className="flex min-h-0 flex-1 flex-col">
      {/* ⚠️⚠️ **NO SECOND HEADER.** The title and the *Új projekt* button are claimed by the chrome's
          own 48px bar through `usePageHeader` above; this screen used to draw its own strip under
          it as well, so the page opened with the word *Projektek* twice, ten pixels apart. The
          mockup has exactly one header, and it is the shell's. */}
      {/* ⚠️ Edge to edge: the mockup drops its `DataList` straight into the content area
      (`markup.html` 455, 462, 594), and the padding that was here is the gap on both
      sides that was reported. */}
      <div className="min-h-0 flex-1">
        <DataList
          columnsKey="projects"
          card={{
            title: 'name',
            context: 'client',
            defaults: { badge: 'status', avatar: 'pm', details: ['done', 'hours', 'risk', 'end'] },
          }}
          isLoading={loading === true}
          actions={
            canCreate
              ? [
                  {
                    label: t('projects.action.create'),
                    icon: 'plus' as const,
                    primary: true,
                    onSelect: () => {
                      setEditing(null);
                      setDialogOpen(true);
                    },
                  },
                ]
              : []
          }
          columns={columns}
          rows={rows}
          search={query.search}
          onSearchChange={query.setSearch}
          sort={query.sort}
          onSortChange={query.onSortChange}
          /**
           * ⚠️ **The mockup's `projListViews`, counted against ALL projects rather than the
           * filtered set** — a count that changes when you pick the view it belongs to tells you
           * nothing about what picking it would do.
           */
          views={[
            { value: 'all', label: t('projects.view.all'), ...countOf(viewCounts, 'all') },
            {
              value: 'active',
              label: t('projects.view.active'),
              ...countOf(viewCounts, 'active'),
            },
            ...(currentMemberId === null || currentMemberId === undefined
              ? []
              : [
                  {
                    value: 'mine',
                    label: t('projects.view.mine'),
                    ...countOf(viewCounts, 'mine'),
                  },
                ]),
            { value: 'late', label: t('projects.view.late'), ...countOf(viewCounts, 'late') },
            /* C60 · *Adathiba* — every project scoring under 85. The mockup's own fifth view. */
            { value: 'data', label: t('projects.view.data'), ...countOf(viewCounts, 'data') },
            /* IP-4 · the only place an archived project is listed. */
            {
              value: 'archived',
              label: t('projects.view.archived'),
              ...countOf(viewCounts, 'archived'),
            },
          ]}
          view={view}
          onViewChange={(next) => {
            /**
             * ⚠️⚠️ **THE VIEW SETS FILTERS AND CLEARS THE OTHER TWO, AND THAT IS A CHANGE.** The
             * mockup treats it as an axis that narrows ON TOP of the status and client filters;
             * every one of the three views is a filter the server already understands, so keeping
             * it as an axis would mean sending *"status = active"* twice under two names and
             * letting them disagree — `?status=paused` under the *Aktív* view has to mean
             * something, and there is no honest answer.
             */
            /* 3 Nov 2026 · a view click is a preset — it overwrites the remembered set. */
            query.applyPreset({
              view: next,
              status: next === 'active' ? 'active' : next === 'archived' ? 'archived' : '',
              /* IP-4 · every view but the archive leaves archived projects out. */
              archived: next === 'archived' ? '' : 'hide',
              pmMemberId: next === 'mine' ? (currentMemberId ?? '') : '',
              late: next === 'late' ? 'true' : '',
              /* C60 · *below* is *under 85*, which is the view's whole definition. */
              risk: next === 'data' ? 'below' : '',
            });
          }}
          filters={[
            {
              key: 'status',
              kind: 'multi' as const,
              label: t('projects.column.status'),
              value: query.filters.status ?? '',
              onChange: (value) => {
                query.setFilter('status', value);
              },
              options: PROJECT_STATUSES.map((value) => ({
                value,
                label: t(statusKey(value)),
              })),
            },
            {
              key: 'client',
              kind: 'multi' as const,
              label: t('projects.column.client'),
              value: query.filters.clientId ?? '',
              onChange: (value) => {
                query.setFilter('clientId', value);
              },
              options: clients.map((client) => ({ value: client.id, label: client.name })),
            },
            /* 17 Oct 2026 · the PM — `GET /projects?pmMemberId=` has carried it since *Az enyémek*. */
            {
              key: 'pm',
              kind: 'multi' as const,
              label: t('projects.field.pm'),
              value: query.filters.pmMemberId ?? '',
              onChange: (value) => {
                query.setFilter('pmMemberId', value);
              },
              /* 3 Nov 2026 · eligible PMs plus the standing ones (`pmFilterMembers`). */
              options: (pmFilterMembers ?? members).map((member) => ({
                value: member.id,
                label: member.name,
              })),
            },
            /**
             * C60 · ⚠️ **Bands, not a number.** The mockup offers exactly these three and
             * nothing else; a free numeric bound would be an API surface no screen asks for and a
             * second home for the two boundaries `riskBand()` already owns.
             */
            {
              key: 'risk',
              kind: 'multi' as const,
              label: t('projects.filter.risk'),
              value: query.filters.risk ?? '',
              onChange: (value) => {
                query.setFilter('risk', value);
              },
              options: RISK_FILTER_VALUES.map((value) => ({
                value,
                label: t(`projects.filter.risk.${value}`),
              })),
            },
            /* ⚠️ **Item 35 · the workspace's own fields**, for the two types with a closed set. */
            ...metafieldFilters(t, metafields ?? [], query.filters, query.setFilter),
          ]}
          /* ⚠️ A row click OPENS the project; editing is in the row menu. The mockup does the same
           * (`open: () => setState({ view: 'project' })`), and it is the right way round: the list
           * is a way into a project far more often than it is a way to rename one. */
          onRowClick={(id) => {
            const project = projects.find((candidate) => candidate.id === id);
            if (project !== undefined) onOpen(project);
          }}
          /**
           * ⚠⚠️ **THE MENU IS NO LONGER GATED ON `canManage`, AND THAT WAS A REAL GAP.** It used
           * to be `canManage ? … : undefined`, so a member without `project.manage` had no row menu
           * at all — including no way to OPEN the project from it, which the first item offers.
           * Pinning made that visible: it is a personal arrangement of somebody's own sidebar and
           * needs nothing but `project.view`, so gating it behind the editing permission would mean
           * only managers could arrange their own chrome.
           *
           * The menu now always renders; only the editing item is conditional.
           */
          rowMenu={(id) => {
            const project = projects.find((candidate) => candidate.id === id);
            if (project === undefined) return [];
            const pinned = sidebar?.isProjectPinned(project.id) ?? false;
            return [
              {
                label: t('projects.action.open'),
                icon: 'arrow-right',
                onSelect: () => {
                  onOpen(project);
                },
              },
              /* ⚠️ Absent entirely when there is no sidebar to pin to — see `useSidebarContext`. */
              ...(sidebar === null
                ? []
                : [
                    {
                      label: pinned ? t('projects.action.unpin') : t('projects.action.pin'),
                      icon: 'star' as const,
                      onSelect: () => {
                        sidebar.setProjectPinned(project.id, !pinned);
                      },
                    },
                  ]),
              ...(canManage
                ? [
                    {
                      label: t('common.edit'),
                      icon: 'pencil' as const,
                      onSelect: () => {
                        setEditing(project);
                        setDialogOpen(true);
                      },
                    },
                  ]
                : []),
              /* The mockup's two ways into this project's tasks — the list, and the Gantt. */
              {
                label: t('projects.action.filterTasks'),
                icon: 'list-checks' as const,
                onSelect: () => {
                  onFilterTasks(project);
                },
              },
              {
                label: t('projects.action.timeline'),
                icon: 'chart-column' as const,
                onSelect: () => {
                  onTimeline(project);
                },
              },
              { type: 'separator' as const },
              {
                label: t('projects.action.report'),
                icon: 'chart-column' as const,
                onSelect: () => {
                  onReport(project);
                },
              },
              /* ⚠️ **Absent, not disabled, without `project.manage`** — an item somebody can never
               * use is a promise the server will refuse. `decisions.md` R-6: UI gating is UX. */
              ...(canManage && project.status !== 'archived'
                ? [
                    {
                      label: t('projects.action.archive'),
                      icon: 'trash' as const,
                      destructive: true,
                      onSelect: () => {
                        askArchive([project], true);
                      },
                    },
                  ]
                : []),
              /* 30 Oct 2026 · an archived row offers the way back, as the project page does. */
              ...(canManage && project.status === 'archived'
                ? [
                    {
                      label: t('projects.action.unarchive'),
                      icon: 'inbox' as const,
                      onSelect: () => {
                        askArchive([project], false);
                      },
                    },
                  ]
                : []),
            ];
          }}
          /**
           * ⚠️ **The selection is passed THROUGH, not counted and thrown away.** The mockup's bulk
           * export toasts *"3 projekt exportálva"* and writes all of them; ours narrows the export
           * request to the selected ids — see `ids` on `dataExportRequestSchema`.
           */
          bulkActions={[
            {
              label: t('projects.action.export'),
              icon: 'download',
              onSelect: (ids) => {
                onExport(projects.filter((project) => ids.includes(project.id)));
              },
            },
            ...(canManage
              ? [
                  {
                    label: t('projects.action.archive'),
                    icon: 'trash' as const,
                    destructive: true,
                    onSelect: (ids: string[]) => {
                      askArchive(
                        projects.filter(
                          (project) => ids.includes(project.id) && project.status !== 'archived',
                        ),
                        true,
                      );
                    },
                  },
                ]
              : []),
          ]}
          empty={{
            icon: 'folder',
            title: t('projects.empty.title'),
            body: t('projects.empty.body'),
            actionLabel: canCreate ? t('projects.action.create') : undefined,
            onAction: canCreate
              ? () => {
                  setEditing(null);
                  setDialogOpen(true);
                }
              : undefined,
          }}
          {...(page === undefined ? {} : { page })}
          labels={listLabels(t)}
        />
      </div>

      {/* 3 Nov 2026 · the archive's reason — asked once for every project in the selection. */}
      <StatusReasonDialog
        open={pendingArchive !== null}
        busy={archiveBusy}
        description={
          pendingArchive === null
            ? ''
            : pendingArchive.projects.length === 1
              ? `${pendingArchive.projects[0]?.name ?? ''} · ${t(
                  pendingArchive.archived ? 'projects.status.archived' : 'projects.status.active',
                )}`
              : t(
                  pendingArchive.archived
                    ? 'projects.statusReason.bulkArchive'
                    : 'projects.statusReason.bulkUnarchive',
                  { count: pendingArchive.projects.length },
                )
        }
        onClose={() => {
          if (!archiveBusy) setPendingArchive(null);
        }}
        onConfirm={(reason) => {
          if (pendingArchive === null) return;
          const act = pendingArchive.archived ? onArchive : onUnarchive;
          setArchiveBusy(true);
          void Promise.resolve(act(pendingArchive.projects, reason))
            .then((landed) => {
              if (landed !== false) setPendingArchive(null);
            })
            .finally(() => {
              setArchiveBusy(false);
            });
        }}
      />

      <ProjectDialog
        /* ⚠️ Item 21 · a client made without leaving the form. Absent when the caller has nowhere
           to create one, which hides the option rather than offering a dead end. */
        {...(onCreateClient === undefined ? {} : { onCreateClient })}
        {...(clientMetafields === undefined ? {} : { clientMetafields })}
        metafields={metafields}
        open={dialogOpen}
        editing={editing}
        clients={clients}
        members={members}
        {...(pmCandidates === undefined ? {} : { pmCandidates })}
        currentMemberId={currentMemberId ?? null}
        serverError={serverError}
        onClose={() => {
          setDialogOpen(false);
        }}
        onSubmit={(draft) => {
          /* ⚠️ 21 Oct 2026 · closed on success only: a refusal stays in the open form, under the
             fields it is about. It never closed at all before — on success either. */
          void (editing === null ? onCreate(draft) : onUpdate(editing, draft)).then((landed) => {
            if (landed) setDialogOpen(false);
          });
        }}
      />
    </div>
  );
}

/*
 * ⚠️⚠️ **THE COMPARATORS THAT STOOD HERE ARE GONE, AND THE SERVER HAS THEM NOW.** They sorted the
 * array this screen had been handed, which item 20's pager turned into *"sort these fifty rows"*
 * presented as the order of all four hundred. `PROJECT_SORT` in `project.service.ts` carries every
 * one of them, including the two that needed an argument: *Készültség* sorts on the PERCENTAGE
 * rather than the done count (40 of 400 is not above 9 of 10), and a project with no tasks sorts
 * BELOW zero rather than at it, because nothing out of nothing is not nothing out of forty.
 */

/**
 * ⚠️ **Two initials from a display name, and the name is `Vezetéknév Keresztnév` in Hungarian.**
 * Taking the first letter of the first two words gives `TG` for *Takács Gábor*, which is the order
 * the mockup's own `ini()` produces.
 */
function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter((part) => part !== '')
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

function statusBadge(status: Project['status']): 'default' | 'secondary' | 'outline' {
  if (status === 'active') return 'default';
  if (status === 'on_hold' || status === 'completed') return 'secondary';
  return 'outline';
}
